import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/auth.store';
import { panelVerificationPath } from '../../utils/panelVerification';
import { publicApi, MenuItemData } from '../../api/public.api';
import {
  Menu,
  X,
  Globe,
  ChevronDown,
  ChevronRight,
  LogIn,
  LayoutDashboard,
  Home,
  Zap,
  BookOpen,
  Smartphone,
  CreditCard,
  FileText,
  Mail,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import BrandMark from '../ui/BrandMark';
import { DEFAULT_HEADER_MENU, mergeHeaderMenu, resolveMenuHref } from '../../utils/menu';

const LANGUAGES = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'zh', label: '中文', dir: 'ltr' },
  { code: 'ko', label: '한국어', dir: 'ltr' },
];

const MENU_ICONS: Record<string, LucideIcon> = {
  Home,
  Zap,
  BookOpen,
  Smartphone,
  CreditCard,
  FileText,
  Mail,
};

const DRAWER_MS = 280;

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const panelLocked = !!panelVerificationPath(user);
  const [menuItems, setMenuItems] = useState<MenuItemData[]>(DEFAULT_HEADER_MENU);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerMounted, setDrawerMounted] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langTriggerRef = useRef<HTMLButtonElement>(null);
  const [langMenuPos, setLangMenuPos] = useState<{ top: number; left: number; width: number } | null>(
    null
  );

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    if (mobileOpen) {
      setDrawerMounted(true);
      const raf = requestAnimationFrame(() => setDrawerVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setDrawerVisible(false);
    const timer = window.setTimeout(() => setDrawerMounted(false), DRAWER_MS);
    return () => window.clearTimeout(timer);
  }, [mobileOpen]);

  useLayoutEffect(() => {
    if (!langOpen) {
      setLangMenuPos(null);
      return;
    }
    const update = () => {
      const el = langTriggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = 176;
      setLangMenuPos({
        top: r.bottom + 8,
        left: Math.max(8, r.right - width),
        width,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [langOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const items = await publicApi.getMenu('header');
        // Merge CMS items with built-in links — never drop defaults when adding custom items
        setMenuItems(mergeHeaderMenu(items));
      } catch {
        setMenuItems(DEFAULT_HEADER_MENU);
      }
    };
    fetchMenu();
  }, [i18n.language]);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    const lang = LANGUAGES.find((l) => l.code === code);
    document.documentElement.dir = lang?.dir || 'ltr';
    document.documentElement.lang = code;
    setLangOpen(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  const isRtl = i18n.dir() === 'rtl';

  const renderMobileNavLink = (item: MenuItemData) => {
    const url = resolveMenuHref(item.url);
    if (!url) return null;
    const isActive = location.pathname === url;
    const isExternal = /^https?:\/\//i.test(url);
    const Icon = (item.icon && MENU_ICONS[item.icon]) || ChevronRight;

    const cn = `flex min-h-[48px] items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors ${
      isActive
        ? 'bg-blue-600/15 text-blue-300 ring-1 ring-inset ring-blue-500/25'
        : 'text-gray-300 active:bg-white/5'
    }`;

    const content = (
      <>
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            isActive ? 'bg-blue-600/20 text-blue-400' : 'bg-white/[0.04] text-gray-500'
          }`}
        >
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <span className="flex-1">{item.label}</span>
        <ChevronRight
          size={16}
          className={`shrink-0 text-gray-600 ${isRtl ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </>
    );

    if (isExternal) {
      return (
        <a
          key={item.id}
          href={url}
          target={item.target}
          rel="noopener noreferrer"
          onClick={closeMobile}
          className={cn}
        >
          {content}
        </a>
      );
    }

    return (
      <Link key={item.id} to={url} onClick={closeMobile} className={cn}>
        {content}
      </Link>
    );
  };

  const mobileMenu =
    drawerMounted &&
    typeof document !== 'undefined' &&
    createPortal(
      <div className="fixed inset-0 z-[9990] lg:hidden" aria-hidden={!mobileOpen}>
        {/* Backdrop */}
        <button
          type="button"
          className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
            drawerVisible ? 'opacity-100' : 'opacity-0'
          }`}
          aria-label={t('common.close_menu')}
          onClick={closeMobile}
        />

        {/* Drawer */}
        <div
          id="mobile-nav-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t('common.menu')}
          className={`absolute inset-y-0 flex w-[min(100vw-2.5rem,20rem)] flex-col bg-gray-950 shadow-2xl transition-transform duration-300 ease-out ${
            isRtl ? 'left-0 border-r border-white/10' : 'right-0 border-l border-white/10'
          } ${drawerVisible ? 'translate-x-0' : isRtl ? '-translate-x-full' : 'translate-x-full'}`}
        >
          {/* Drawer header */}
          <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <div className="flex items-center gap-2.5">
              <BrandMark variant="icon" size="h-8 w-8" to="" />
              <span className="text-sm font-semibold text-white">{t('common.menu')}</span>
            </div>
            <button
              type="button"
              onClick={closeMobile}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-400 transition hover:bg-white/5 hover:text-white"
              aria-label={t('common.close_menu')}
            >
              <X size={22} />
            </button>
          </div>

          {/* Nav links */}
          <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3">
            <ul className="space-y-1">{menuItems.map((item) => (
              <li key={item.id}>{renderMobileNavLink(item)}</li>
            ))}</ul>
          </nav>

          {/* Language */}
          <div className="shrink-0 border-t border-white/10 px-4 py-4">
            <p className="mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              <Globe size={13} />
              {t('common.language')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => changeLanguage(lang.code)}
                  className={`min-h-[40px] rounded-lg px-3 py-2 text-sm font-medium transition ${
                    i18n.language === lang.code
                      ? 'bg-blue-600/20 text-blue-300 ring-1 ring-inset ring-blue-500/30'
                      : 'bg-white/[0.04] text-gray-400 hover:bg-white/[0.07] hover:text-gray-200'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Auth footer */}
          <div className="shrink-0 space-y-2.5 border-t border-white/10 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {isAuthenticated && !panelLocked ? (
              <Link
                to="/dashboard"
                onClick={closeMobile}
                className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/20"
              >
                <LayoutDashboard size={18} />
                {t('common.dashboard')}
              </Link>
            ) : !isAuthenticated ? (
              <>
                <Link
                  to="/register"
                  onClick={closeMobile}
                  className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/20"
                >
                  {t('home.cta_primary')}
                  <ArrowRight size={16} className={isRtl ? 'rotate-180' : ''} />
                </Link>
                <Link
                  to="/login"
                  onClick={closeMobile}
                  className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-white/15 px-4 py-3 text-sm font-medium text-gray-300 transition hover:border-white/25 hover:bg-white/[0.03]"
                >
                  <LogIn size={16} />
                  {t('common.login')}
                </Link>
              </>
            ) : null}
          </div>
        </div>
      </div>,
      document.body
    );

  const desktopLangMenu =
    langOpen &&
    langMenuPos &&
    typeof document !== 'undefined' &&
    createPortal(
      <>
        <button
          type="button"
          className="fixed inset-0 z-[9998] hidden cursor-default bg-transparent lg:block"
          aria-label="Close language menu"
          onClick={() => setLangOpen(false)}
        />
        <div
          role="listbox"
          className="fixed z-[9999] hidden overflow-hidden rounded-xl border border-gray-700 bg-gray-800 shadow-xl lg:block"
          style={{ top: langMenuPos.top, left: langMenuPos.left, width: langMenuPos.width }}
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              onClick={() => changeLanguage(lang.code)}
              className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                i18n.language === lang.code
                  ? 'bg-blue-600/20 text-blue-400'
                  : 'text-gray-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </>,
      document.body
    );

  return (
    <header className="sticky top-0 z-[100] flex-shrink-0 isolate border-b border-white/[0.06] bg-gray-950 pt-[env(safe-area-inset-top)] shadow-lg shadow-black/10">
      <div className="relative z-[101] mx-auto max-w-7xl pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:pl-6 sm:pr-6 lg:pl-8 lg:pr-8">
        <div className="flex min-h-[44px] items-center justify-between gap-3 py-2 sm:min-h-[52px] sm:py-3 lg:py-4">
          <BrandMark
            variant="logo"
            size="h-9 sm:h-10 lg:h-11"
            to="/"
            className="max-w-[9.5rem] shrink-0 sm:max-w-[10.5rem] lg:h-auto lg:max-w-none lg:w-40"
          />

          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-1 lg:flex">
            {(menuItems ?? []).map((item) => {
              const url = resolveMenuHref(item.url);
              if (!url) return null;
              const isActive = location.pathname === url;
              const isExternal = /^https?:\/\//i.test(url);
              const cn = `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive ? 'bg-white/15 text-white' : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`;
              return isExternal ? (
                <a key={item.id} href={url} target={item.target} rel="noopener noreferrer" className={cn}>
                  {item.label}
                </a>
              ) : (
                <Link key={item.id} to={url} target={item.target} className={cn}>
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <div className="relative">
              <button
                ref={langTriggerRef}
                type="button"
                aria-expanded={langOpen}
                aria-haspopup="listbox"
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-gray-300 transition-all hover:bg-white/10 hover:text-white"
              >
                <Globe size={16} />
                <span>{currentLang.label}</span>
                <ChevronDown size={14} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {isAuthenticated && !panelLocked ? (
              <Link
                to="/dashboard"
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:scale-[1.02] hover:shadow-blue-600/40"
              >
                <LayoutDashboard size={16} />
                {t('common.dashboard')}
              </Link>
            ) : !isAuthenticated ? (
              <>
                <Link
                  to="/login"
                  className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-medium text-gray-300 transition-all hover:border-white/20 hover:bg-white/10 hover:text-white"
                >
                  <LogIn size={16} />
                  {t('common.login')}
                </Link>
                <Link
                  to="/register"
                  className="px-2 py-2.5 text-sm font-medium text-gray-400 transition hover:text-white"
                >
                  {t('common.register')}
                </Link>
              </>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="relative z-[102] flex h-10 w-10 shrink-0 touch-manipulation items-center justify-center rounded-xl border border-white/10 text-gray-300 transition hover:border-white/20 hover:bg-white/5 hover:text-white lg:hidden"
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
            aria-label={mobileOpen ? t('common.close_menu') : t('common.open_menu')}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {mobileMenu}
      {desktopLangMenu}
    </header>
  );
}

